import * as React from "react";

import { useViewportStore } from "@agent-ui/features/page-renderer/store/viewportStore";

type DragEdge = "right" | "bottom" | "corner";

function ResizableViewport({ children }: React.PropsWithChildren) {
  const { customWidth, customHeight, viewportMode, setCustomDimensions } =
    useViewportStore();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState<DragEdge | null>(null);
  const dragStartRef = React.useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const handleMouseDown = React.useCallback(
    (edge: DragEdge, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
      };
      setIsDragging(edge);
    },
    [],
  );

  React.useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) {
        return;
      }
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;

      const newWidth =
        isDragging === "right" || isDragging === "corner"
          ? Math.max(200, Math.round(start.width + dx * 2))
          : customWidth;
      const newHeight =
        isDragging === "bottom" || isDragging === "corner"
          ? Math.max(200, Math.round(start.height + dy))
          : customHeight;
      setCustomDimensions({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsDragging(null);
      dragStartRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, setCustomDimensions, customWidth, customHeight]);

  const isConstrained =
    viewportMode === "preset" || customWidth != null || customHeight != null;

  if (!isConstrained) {
    return (
      <div className="flex-1 relative w-full min-w-0 min-h-0 overflow-hidden rounded-b-lg">
        {children}
      </div>
    );
  }

  return (
    <div className="flex-1 relative w-full min-w-0 min-h-0 flex items-start justify-center overflow-hidden bg-subtle p-2">
      <div
        ref={containerRef}
        className="relative mx-auto min-w-0"
        style={{
          width: customWidth != null ? `min(${customWidth}px, 100%)` : "100%",
          height: customHeight != null ? `${customHeight}px` : "100%",
          maxHeight: "100%",
        }}
      >
        <div className="absolute inset-0 bg-white overflow-hidden">
          {children}
        </div>

        {/* Right edge handle */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize width"
          onMouseDown={(e) => handleMouseDown("right", e)}
          className="absolute top-0 -right-2 w-4 h-full cursor-ew-resize z-10 flex items-center justify-center"
        >
          <div className="w-1 h-8 rounded-full bg-border" />
        </div>

        {/* Bottom edge handle */}
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize height"
          onMouseDown={(e) => handleMouseDown("bottom", e)}
          className="absolute -bottom-2 left-0 h-4 w-full cursor-ns-resize z-10 flex items-center justify-center"
        >
          <div className="h-1 w-8 rounded-full bg-border" />
        </div>

        {/* Corner handle */}
        <div
          role="separator"
          aria-label="Resize width and height"
          onMouseDown={(e) => handleMouseDown("corner", e)}
          className="absolute -bottom-2 -right-2 size-4 cursor-nwse-resize z-20 flex items-center justify-center"
        >
          <div className="size-2 rounded-full bg-border" />
        </div>
      </div>
    </div>
  );
}

export default ResizableViewport;
